import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Cloud, Upload, Download, Trash2, RefreshCw, X, Search, Globe, Users, User, AlertTriangle, ExternalLink } from "lucide-react";

const SCOPE_META = {
    common: { label: "COMMON", tone: "border-emerald-300/40 bg-emerald-300/10 text-emerald-300", Icon: Globe },
    multi_client_shared: { label: "SHARED", tone: "border-[#7C3AED]/50 bg-[#7C3AED]/15 text-[#c7a6ff]", Icon: Users },
    client_owned: { label: "SINGLE TENANT", tone: "border-amber-300/40 bg-amber-300/5 text-amber-200", Icon: User },
};
const REGIONS = ["us-east-1", "us-west-2", "eu-west-1", "eu-central-1", "ap-south-1", "ap-southeast-1"];

function fmtSize(n) {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function formatErr(d) { if (!d) return "Error"; if (typeof d === "string") return d; if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" "); return String(d); }

export default function S3FilesPage() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ q: "", classification: "", data_region: "" });
    const [uploadOpen, setUploadOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
            params.set("limit", "200");
            const [files, st] = await Promise.all([
                api.get(`/admin/s3/files?${params.toString()}`),
                api.get(`/admin/s3/status`),
            ]);
            setItems(files.data.items || []);
            setStatus(st.data);
        } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const download = async (file) => {
        try {
            const { data } = await api.get(`/admin/s3/files/${file.id}`);
            window.open(data.presigned_url, "_blank");
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        }
    };

    const remove = async (file) => {
        if (!window.confirm(`Delete "${file.original_filename || file.s3_key}"? This removes the metadata AND the S3 object.`)) return;
        try {
            await api.delete(`/admin/s3/files/${file.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    };

    return (
        <div className="px-8 py-8" data-testid="s3-files-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2"><Cloud className="w-3 h-3" /> AWS S3 Storage</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">S3 Files</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Files stored in your AWS S3 bucket. Metadata is tenant-scoped via the Phase K
                        ownership envelope; downloads use 1-hour presigned URLs so the bucket itself
                        remains private.
                    </p>
                </div>
                <button onClick={() => setUploadOpen(true)} disabled={!status?.configured} data-testid="s3-upload"
                    className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white px-3 py-2 rounded-md text-sm flex items-center gap-1.5 disabled:opacity-40">
                    <Upload className="w-4 h-4" /> Upload
                </button>
            </div>

            {/* Status banner */}
            {status && !status.configured && (
                <div className="mb-5 p-4 border border-amber-300/30 bg-amber-300/5 rounded-md flex items-start gap-3" data-testid="s3-not-configured">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-300" strokeWidth={1.8} />
                    <div className="text-xs text-white/85">
                        <div className="font-medium text-amber-200">S3 is not configured</div>
                        <div className="text-white/60 mt-1">
                            Set <code>AWS_ACCESS_KEY_ID</code>, <code>AWS_SECRET_ACCESS_KEY</code>,
                            <code> AWS_REGION</code>, and <code>S3_BUCKET_NAME</code> in
                            <code> /app/backend/.env</code>, then restart the backend. Until then, listing works on
                            existing metadata but uploads + presigned downloads are disabled.
                        </div>
                    </div>
                </div>
            )}
            {status?.configured && (
                <div className="mb-5 p-3 border border-emerald-300/30 bg-emerald-300/5 rounded-md text-xs font-mono text-white/80" data-testid="s3-configured">
                    Bucket <span className="text-white">{status.bucket}</span> · region {status.region} · presigned URL TTL {status.presigned_ttl_seconds}s
                </div>
            )}

            {/* Filters */}
            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-5 grid grid-cols-4 gap-3">
                <input type="text" placeholder="Search filename / key…" value={filters.q}
                    onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                    data-testid="s3-search"
                    className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                <select value={filters.classification} onChange={(e) => setFilters((p) => ({ ...p, classification: e.target.value }))}
                    data-testid="s3-classification"
                    className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                    <option value="">All classifications</option>
                    <option value="client_owned">client_owned</option>
                    <option value="multi_client_shared">multi_client_shared</option>
                    <option value="common">common</option>
                </select>
                <select value={filters.data_region} onChange={(e) => setFilters((p) => ({ ...p, data_region: e.target.value }))}
                    data-testid="s3-region"
                    className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                    <option value="">All regions</option>
                    {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button type="submit" data-testid="s3-apply" className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md flex items-center gap-2 justify-center">
                    <Search className="w-4 h-4" /> Apply
                </button>
            </form>

            {/* List */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                    <thead><tr className="text-left">
                        <th className="label-xs px-5 py-3">Filename</th>
                        <th className="label-xs px-5 py-3">File ID</th>
                        <th className="label-xs px-5 py-3">Ownership</th>
                        <th className="label-xs px-5 py-3">Size</th>
                        <th className="label-xs px-5 py-3">Region</th>
                        <th className="label-xs px-5 py-3">Uploaded</th>
                        <th className="label-xs px-5 py-3 text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan={7} className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading…</td></tr>
                            : items.length === 0 ? <tr><td colSpan={7} className="px-5 py-16 text-center">
                                <Cloud className="w-10 h-10 text-white/20 mx-auto mb-3" />
                                <div className="text-sm text-white/60">No files</div>
                            </td></tr>
                            : items.map((it, idx) => {
                                const meta = SCOPE_META[it.classification] || SCOPE_META.client_owned;
                                const ScopeIcon = meta.Icon;
                                return (
                                    <tr key={it.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`s3-row-${idx}`}>
                                        <td className="px-5 py-3 max-w-[300px] truncate" title={it.original_filename}>{it.original_filename}</td>
                                        <td className="px-5 py-3 font-mono text-[11px] text-white/55 max-w-[180px] truncate" title={it.id}>{it.id}</td>
                                        <td className="px-5 py-3">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${meta.tone}`}>
                                                <ScopeIcon className="w-3 h-3" /> {meta.label}
                                            </span>
                                            {(it.owners || []).length > 0 && (
                                                <div className="text-[11px] font-mono text-white/45 mt-1">{it.owners.join(", ")}</div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs text-white/70">{fmtSize(it.size_bytes)}</td>
                                        <td className="px-5 py-3 font-mono text-xs text-white/70">{it.data_region}</td>
                                        <td className="px-5 py-3 font-mono text-xs text-white/55">{new Date(it.uploaded_at).toLocaleString()}</td>
                                        <td className="px-5 py-3 text-right">
                                            <div className="flex justify-end gap-1.5">
                                                <button onClick={() => download(it)} data-testid={`s3-download-${idx}`} title="Download via presigned URL"
                                                    className="p-1.5 rounded hover:bg-emerald-500/15 text-emerald-300"><Download className="w-3.5 h-3.5" /></button>
                                                <button onClick={() => remove(it)} data-testid={`s3-delete-${idx}`}
                                                    className="p-1.5 rounded hover:bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>

            {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} onDone={() => { setUploadOpen(false); load(); }} />}
        </div>
    );
}

function UploadModal({ onClose, onDone }) {
    const fileRef = useRef(null);
    const [classification, setClassification] = useState("client_owned");
    const [owners, setOwners] = useState("");
    const [region, setRegion] = useState("us-east-1");
    const [description, setDescription] = useState("");
    const [uploading, setUploading] = useState(false);

    const upload = async () => {
        if (!fileRef.current?.files?.[0]) {
            toast.error("Pick a file");
            return;
        }
        const fd = new FormData();
        fd.append("file", fileRef.current.files[0]);
        if (description) fd.append("description", description);
        fd.append("classification", classification);
        fd.append("data_region", region);
        if (owners) fd.append("owners", owners);
        setUploading(true);
        try {
            await api.post("/admin/s3/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Uploaded");
            onDone();
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className="w-full max-w-md bg-[#0a0a0a] border border-white/15 rounded-md" onClick={(e) => e.stopPropagation()} data-testid="s3-upload-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <div className="font-display text-lg tracking-tight">Upload to S3</div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <div className="label-xs mb-1.5">File</div>
                        <input type="file" ref={fileRef} data-testid="s3-file" className="w-full text-xs text-white/70" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Description</div>
                        <input value={description} onChange={(e) => setDescription(e.target.value)}
                            data-testid="s3-description"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Classification</div>
                        <select value={classification} onChange={(e) => setClassification(e.target.value)} data-testid="s3-upload-classification"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                            <option value="client_owned">client_owned</option>
                            <option value="multi_client_shared">multi_client_shared</option>
                            <option value="common">common (cross-tenant)</option>
                        </select>
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Owners (comma-separated client_ids)</div>
                        <input value={owners} onChange={(e) => setOwners(e.target.value)}
                            placeholder={classification === "common" ? "(leave blank for common)" : "clientA, clientB"}
                            disabled={classification === "common"}
                            data-testid="s3-upload-owners"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono disabled:opacity-40" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Data region</div>
                        <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="s3-upload-region"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                </div>
                <div className="px-5 py-4 border-t border-white/10 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button onClick={upload} disabled={uploading} data-testid="s3-upload-submit"
                        className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md hover:bg-[#7C3AED]/90 disabled:opacity-40">
                        {uploading ? "Uploading…" : "Upload"}
                    </button>
                </div>
            </div>
        </div>
    );
}
